<template>
  <validation-observer ref="refFormObserver" #default="{ invalid }">
    <b-modal
        :id="modalId"
        v-model="isModalOpened"
        :ok-disabled="invalid || loading"
        :title="modalTitle"
        cancel-title="Annuler"
        ok-title="Enregistrer"
        size="lg"
        @hidden="resetForm"
        @ok="handleSaving"
        @show="resetForm"
    >
      <b-form novalidate>
        <div>
          <!-- Amount -->
          <validation-provider
              #default="{ errors }"
              name="montant"
              rules="required"
          >
            <b-form-group label="Montant *" label-for="modal-amount">
              <b-form-input
                  id="modal-amount"
                  v-model="form.amount"
                  placeholder="Entrez le montant"
                  required
                  type="number"
              ></b-form-input>
            </b-form-group>
            <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
              {{ errors[0] }}
            </b-form-invalid-feedback>
          </validation-provider>

          <!-- Operation type -->
          <validation-provider
              #default="{ errors }"
              :rules="isExit ? 'required' : ''"
              name="type d'opération"
          >
            <b-form-group
                v-if="isExit"
                label="Type de sortie *"
                label-for="modal-expense-type"
            >
              <b-form-select
                  id="modal-expense-type"
                  v-model="form.expense_type"
                  :options="expenseTypes"
                  name="expenseType"
              />
            </b-form-group>
            <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
              {{ errors[0] }}
            </b-form-invalid-feedback>
          </validation-provider>

          <!-- Liste des projets -->
          <div v-if="isExit && isProjectDebitExpense">
            <div
                v-if="!canMakeProjectExpense"
                class="alert alert-warning"
                role="alert"
            >
              <strong>Attention :</strong> Vous n'avez pas la permission
              d'effectuer des sorties de caisse pour les projets. Veuillez
              contacter un administrateur.
            </div>
            <div v-else>
              <validation-provider
                  #default="{ errors }"
                  name="project"
                  rules="required"
              >
                <b-form-group
                    label="Sélectionnez un projet *"
                    label-for="modal-project"
                >
                  <v-select
                      v-model="form.concerned_project"
                      :clearable="false"
                      :options="projectsList"
                      :reduce="(o) => o.value"
                      input-id="modal-project"
                      placeholder="Sélectionnez ou Recherchez votre projet"
                      @input="getExpensesList"
                  >
                    <template v-slot:no-options="{ search, searching }">
                      <template v-if="searching">
                        Pas de résultat pour <em>{{ search }}</em
                      >.
                      </template>
                      <em v-else style="opacity: 0.5"
                      >Recherche un projet...</em
                      >
                    </template>
                  </v-select>
                </b-form-group>
                <b-form-invalid-feedback
                    :state="errors.length > 0 ? false : null"
                >
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </validation-provider>
            </div>
          </div>

          <!-- Destination Account (Transfer only) -->
          <validation-provider
              v-if="isTransfer"
              #default="{ errors }"
              name="compte de destination"
              rules="required"
          >
            <b-form-group
                label="Compte de destination *"
                label-for="modal-destination-account"
            >
              <v-select
                  v-model="form.destination_account"
                  :clearable="false"
                  :options="bankAccountsList"
                  :reduce="(o) => o.value"
                  label="label"
                  input-id="modal-destination-account"
                  placeholder="Sélectionnez le compte de destination"
              >
                <template v-slot:no-options="{ search, searching }">
                  <template v-if="searching">
                    Pas de résultat pour <em>{{ search }}</em>.
                  </template>
                  <em v-else style="opacity: 0.5">Recherche un compte...</em>
                </template>
              </v-select>
              <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>

            <!-- Balance preview for transfer -->
            <div v-if="form.destination_account && form.amount > 0" class="alert alert-info mt-1">
              <div class="d-flex justify-content-between">
                <span><strong>Solde source après transfert:</strong> {{ formatBalance(sourceBalanceAfterTransfer) }}</span>
                <span><strong>Solde destination après transfert:</strong> {{ formatBalance(destinationBalanceAfterTransfer) }}</span>
              </div>
            </div>
          </validation-provider>

          <!-- Justification -->
          <validation-provider
              #default="{ errors }"
              name="designation"
              rules="required"
          >
            <vue-autosuggest
                v-if="isProjectDebitExpense"
                v-model="form.designation"
                :input-props="inputProps"
                :limit="designationSuggestionLimit"
                :suggestions="designationOptions"
                @input="onDesignationChange"
                @selected="onSelectedDesignation"
            >
              <template slot-scope="{ suggestion }">
                <div class="d-flex align-items-center">
                  <div class="detail">
                    <b-card-text>
                      {{ suggestion.item.designation }}
                    </b-card-text>
                  </div>
                </div>
              </template>
            </vue-autosuggest>
            <b-form-group
                v-else
                :description="isTransfer ? 'Indiquez le motif du transfert' : 'Indiquez le libellé de votre dépense'"
                :label="isTransfer ? 'Motif du transfert' : 'Justification'"
                label-for="designation"
            >
              <b-form-textarea
                  v-model="form.designation"
                  :placeholder="isTransfer ? 'Motif du transfert' : 'Ajoutez des détails supplémentaires sur l\'opération si nécessaire'"
                  rows="3"
              />
            </b-form-group>
            <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
              {{ errors[0] }}
            </b-form-invalid-feedback>
          </validation-provider>
        </div>
      </b-form>
    </b-modal>
  </validation-observer>
</template>

<script>
import {mapGetters, mapMutations} from "vuex";
import {
  BCardText,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BFormSelect,
  BFormTextarea
} from "bootstrap-vue";
import {ValidationObserver, ValidationProvider} from "vee-validate";
import {VueAutosuggest} from "vue-autosuggest";

import vSelect from "vue-select";
import {onFieldChange} from "@/utils/filter";

import store from "@/store";
import {ADMIN_EXPENSE, CREDIT_MODAL, DEBIT_MODAL, PROJECT_EXPENSE, TRANSFER_MODAL} from '@/constants/finance'
import {amountToCurrency} from "@/utils/formatAmount";


// Expense type

export default {
  name: "BankOperationModal",
  components: {
    ValidationObserver,
    BFormInvalidFeedback,
    ValidationProvider,
    BCardText,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BFormGroup,
    BForm,
    VueAutosuggest,
    vSelect,
  },
  props: {
    modalId: {
      type: String,
      required: true,
    },
    isOpened: {
      type: Boolean,
      required: true,
    },
    modalType: {
      type: String, // either 'entry', 'exit', or 'transfer'
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    currentBankData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isFetchingProjects: false,
      isFetchingBankAccounts: false,

      form: {
        amount: null,
        expense_type: null,
        concerned_project: null,
        destination_account: null,
        designation: "",
      },
      expenseTypes: [
        {text: "Sortie Administrative", value: ADMIN_EXPENSE},
        {text: "Sortie Projet", value: PROJECT_EXPENSE},
      ],
      projectsList: [],
      bankAccountsList: [],
      // expense display properties
      designationOptions: [],
      designationSuggestionLimit: 10,
    };
  },
  watch: {
    isOpened(val) {
      if (val) {
        this.resetForm();
      }
    },
    isProjectDebitExpense(newVal) {
      if (newVal) {
        this.form.concerned_project = null;
        this.fetchProjects();
      } else {
        this.form.concerned_project = null;
      }
    },
    errorsData: function (val) {
      if (val && Object.keys(val).length > 0) {
        this.handleErrors(val);
      }
    },
  },
  computed: {
    modalTitle() {
      if (this.modalType === TRANSFER_MODAL) {
        return "Effectuer un Transfert";
      }
      return this.modalType === CREDIT_MODAL
          ? "Effectuer un Encaissement"
          : "Effectuer un Décaissement";
    },
    isModalOpened: {
      get() {
        return this.isOpened;
      },
      set(value) {
        this.$emit("update:isOpened", value);
      },
    },
    loading: {
      get() {
        return this.isLoading;
      },
      set(value) {
        this.$emit("update:isLoading", value);
      },
    },
    isExit() {
      return this.modalType === DEBIT_MODAL;
    },
    isTransfer() {
      return this.modalType === TRANSFER_MODAL;
    },
    inputProps() {
      return {
        id: "autosuggest__input",
        class: "form-control",
        placeholder: "Entrer une désignation...",
        disabled: this.isProjectDebitExpense && !this.canMakeProjectExpense,
      };
    },
    ...mapGetters("project", {
      expensesData: "expenses",
    }),
    ...mapGetters("fundHistory", {
      errorsData: "errors",
      accountsList: "accounts",
    }),
    isProjectDebitExpense() {
      return this.form.expense_type === PROJECT_EXPENSE;
    },
    canMakeProjectExpense() {
      return this.$can("make_withdraws", "Caisse Générale");
    },
    selectedDestinationAccount() {
      if (this.form.destination_account) {
        return this.bankAccountsList.find(
          (acc) => acc.value === this.form.destination_account
        );
      }
      return null;
    },
    sourceBalanceAfterTransfer() {
      if (this.currentBankData && this.form.amount) {
        return this.currentBankData.amount - parseFloat(this.form.amount || 0);
      }
      return 0;
    },
    destinationBalanceAfterTransfer() {
      if (this.selectedDestinationAccount && this.form.amount) {
        return this.selectedDestinationAccount.amount + parseFloat(this.form.amount || 0);
      }
      return 0;
    },
  },
  methods: {
    onDesignationChange(text) {
      this.designationOptions = onFieldChange(
          text,
          this.expensesData,
          this.designationSuggestionLimit,
          "designation"
      );
    },
    ...mapMutations("fundHistory", {
      setError: "SET_ERRORS",
    }),
    formatBalance(balance) {
      return amountToCurrency(balance, "fr-FR", null) + " XOF";
    },
    resetForm() {
      this.setError(null);
      this.form = {
        amount: null,
        expense_type: null,
        concerned_project: null,
        destination_account: null,
        designation: "",
      };
      if (this.isTransfer) {
        this.fetchBankAccounts();
      }
    },
    handleSaving(ev) {
      ev.preventDefault();
      this.onSubmit();
    },
    onSubmit() {
      this.$refs.refFormObserver
          .validate()
          .then((success) => {
            if (success) {
              this.loading = true;

              this.$emit("submit", this.form);
            }
          })
    },
    fetchProjects() {
      if (this.modalType === CREDIT_MODAL) return;

      this.isFetchingProjects = true;
      store.dispatch("project/allProjects", {props: ['project_code']}).then((projects) => {
        if (projects.length > 0) {
          this.projectsList = projects.map((p) => {
            return {label: p.project_code, value: p.uid};
          });
        } else {
          this.projectsList = [
            {label: "Aucun projet retrouvé...", value: null},
          ];
        }
      });
      this.isFetchingProjects = false;
    },
    getExpensesList(uid) {
      store.dispatch("project/fetchExpenses", {projectId: uid});
    },
    onSelectedDesignation(option) {
      this.form.designation = option.item.designation;
    },
    fetchBankAccounts() {
      if (this.modalType !== TRANSFER_MODAL) return;

      this.isFetchingBankAccounts = true;
      store.dispatch("fundHistory/getAccounts").then(() => {
        const accounts = this.accountsList;
        if (accounts && accounts.length > 0) {
          // Filter out current account from destination list
          this.bankAccountsList = accounts
            .filter((acc) => this.currentBankData && acc.uid !== this.currentBankData.uid)
            .map((acc) => ({
              label: acc.name,
              value: acc.uid,
              amount: acc.amount,
            }));
        } else {
          this.bankAccountsList = [
            { label: "Aucun compte retrouvé...", value: null },
          ];
        }
        this.isFetchingBankAccounts = false;
      });
    },
    handleErrors(errors) {
      for (const [field, message] of Object.entries(errors)) {
        const currentMessage = typeof message === 'object' ? message[0] : message;
          this.$refs.refFormObserver.setErrors({[field]: currentMessage });
      }
    },
    getValidationState(validationContext) {
      return validationContext.errors.length > 0 ? false : null;
    },
  },
};
</script>
